import { Users as UsersIcon, Plus, Search, Mail, Shield, MoreVertical, AlertCircle, MailWarning, Check, X, Building2, Wifi, UserPlus } from 'lucide-react'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { useApproveOrphanUser, useOrphanUsers, useRejectOrphanUser, useUsers } from '../../hooks/useUsers'
import { useAdUsers } from '../../hooks/useAdDomains'
import UserModal from '../../components/Modal/UserModal'
import AssociateExistingUserModal from '../../components/Modal/AssociateExistingUserModal'
import { useState } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { formatDate } from '../../utils/format'

const roleLabels = {
  owner: { label: 'Owner', color: 'badge-primary' },
  admin: { label: 'Admin', color: 'badge-secondary' },
  operator: { label: 'Operator', color: 'badge-success' },
  viewer: { label: 'Viewer', color: 'badge-gray' },
}

export default function Users() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const isGlobalUser = Boolean(user?.isGlobalUser || user?.IsGlobalUser)
  const { data: users, isLoading, error } = useUsers()
  const { data: orphanUsers, isLoading: isOrphansLoading } = useOrphanUsers(isGlobalUser)
  const { data: adUsers } = useAdUsers()
  const adUserIds = new Set((adUsers || []).map((u) => u.userId))
  const approveOrphanMutation = useApproveOrphanUser()
  const rejectOrphanMutation = useRejectOrphanUser()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isAssociateModalOpen, setIsAssociateModalOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [roleFilter, setRoleFilter] = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')
  const [activeTab, setActiveTab] = useState('platform') // 'platform' | 'vpn'

  const handleAdd = () => {
    setSelectedUser(null)
    setIsModalOpen(true)
  }

  const handleEdit = (u) => {
    setSelectedUser(u)
    setIsModalOpen(true)
  }

  // Separa usuários em duas listas: plataforma (web/manual) e VPN-only (Connect.IO via AD)
  const platformUsers = (users || []).filter((u) => !(u.isVpnOnly || u.IsVpnOnly))
  const vpnUsers = (users || []).filter((u) => (u.isVpnOnly || u.IsVpnOnly))

  const baseList = activeTab === 'vpn' ? vpnUsers : platformUsers

  const filteredUsers = baseList.filter((u) => {
    const matchesSearch = u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.email?.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesRole = roleFilter === 'all' || u.role === roleFilter.toLowerCase()
    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === 'enabled' && u.status === 'active') ||
      (statusFilter === 'disabled' && u.status !== 'active')
    return matchesSearch && matchesRole && matchesStatus
  })

  const stats = {
    total: platformUsers.length,
    active: platformUsers.filter((u) => u.status === 'active').length,
    admins: platformUsers.filter((u) => u.role === 'admin' || u.role === 'owner').length,
    semAcesso: platformUsers.filter((u) => u.status !== 'active').length,
    vpn: vpnUsers.length,
  }

  const handleApproveOrphan = async (userId) => {
    await approveOrphanMutation.mutateAsync(userId)
  }

  const handleRejectOrphan = async (userId) => {
    await rejectOrphanMutation.mutateAsync(userId)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600">{t('users.loading')}</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-600 flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          {t('users.loadError')}: {error.message}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('users.title')}</h1>
          <p className="mt-1 text-sm text-gray-600">
            {t('users.subtitle')}
          </p>
        </div>
        {activeTab === 'platform' && (
          <div className="flex items-center gap-2">
            {isGlobalUser && (
              <button onClick={() => setIsAssociateModalOpen(true)} className="btn btn-secondary">
                <UserPlus className="w-4 h-4" />
                {t('users.associateExisting.button')}
              </button>
            )}
            <button onClick={handleAdd} className="btn btn-primary">
              <Plus className="w-4 h-4" />
              {t('users.create')}
            </button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200">
        <button
          type="button"
          onClick={() => { setActiveTab('platform'); setRoleFilter('all'); setStatusFilter('all'); }}
          className={clsx(
            'px-4 py-2 text-sm font-medium flex items-center gap-2',
            activeTab === 'platform'
              ? 'text-theme-primary border-b-2 border-theme-primary'
              : 'text-gray-600 hover:text-gray-900',
          )}
        >
          <UsersIcon className="w-4 h-4" />
          {t('users.tabs.platform', 'Usuários da plataforma')}
          <span className="ml-1 text-xs bg-gray-100 text-gray-700 rounded px-1.5 py-0.5">{platformUsers.length}</span>
        </button>
        <button
          type="button"
          onClick={() => { setActiveTab('vpn'); setRoleFilter('all'); setStatusFilter('all'); }}
          className={clsx(
            'px-4 py-2 text-sm font-medium flex items-center gap-2',
            activeTab === 'vpn'
              ? 'text-theme-primary border-b-2 border-theme-primary'
              : 'text-gray-600 hover:text-gray-900',
          )}
        >
          <Wifi className="w-4 h-4" />
          {t('users.tabs.vpn', 'Usuários VPN (AD via Connect.IO)')}
          <span className="ml-1 text-xs bg-gray-100 text-gray-700 rounded px-1.5 py-0.5">{vpnUsers.length}</span>
        </button>
      </div>

      {activeTab === 'vpn' && (
        <p className="text-xs text-gray-500 -mt-2">
          {t('users.tabs.vpnHint', 'Usuários autenticados pelo Connect.IO via Active Directory. Não logam no painel; o acesso a recursos é definido pelo firewall de cada Managed Device (origem AdGroup ou role do tenant).')}
        </p>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <div className="card p-4">
          <div className="text-2xl font-bold text-gray-900">{activeTab === 'vpn' ? stats.vpn : stats.total}</div>
          <div className="text-sm text-gray-600 mt-1">{t('users.stats.total')}</div>
        </div>
        <div className="card p-4">
          <div className="text-2xl font-bold text-green-600">{stats.active}</div>
          <div className="text-sm text-gray-600 mt-1">{t('users.stats.active')}</div>
        </div>
        <div className="card p-4">
          <div className="text-2xl font-bold text-theme-primary">{stats.admins}</div>
          <div className="text-sm text-gray-600 mt-1">{t('users.stats.admins')}</div>
        </div>
        <div className="card p-4">
          <div className="text-2xl font-bold text-amber-600">{stats.semAcesso}</div>
          <div className="text-sm text-gray-600 mt-1">{t('users.stats.noAccess')}</div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex items-center gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder={t('users.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 input"
          />
        </div>
        <select
          className="input w-48"
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
        >
          <option value="all">{t('users.rolesFilter')}</option>
          <option value="owner">Owner</option>
          <option value="admin">Admin</option>
          <option value="operator">Operator</option>
          <option value="viewer">Viewer</option>
        </select>
        <select
          className="input w-48"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">{t('users.statusFilter.all')}</option>
          <option value="enabled">{t('users.statusFilter.enabled')}</option>
          <option value="disabled">{t('users.statusFilter.disabled')}</option>
        </select>
      </div>

      {isGlobalUser && (
        <div className="card overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 bg-amber-50">
            <h2 className="text-sm font-semibold text-amber-900">
              {t('users.orphans.title')}
            </h2>
            <p className="text-xs text-amber-800 mt-1">
              {t('users.orphans.subtitle')}
            </p>
          </div>
          {isOrphansLoading ? (
            <div className="p-4 text-sm text-gray-600">{t('users.orphans.loading')}</div>
          ) : !orphanUsers?.length ? (
            <div className="p-4 text-sm text-gray-600">{t('users.orphans.empty')}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left py-2 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">{t('users.fields.name')}</th>
                    <th className="text-left py-2 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">{t('users.orphans.registered')}</th>
                    <th className="text-left py-2 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">{t('users.headerActions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {orphanUsers.map((orphan) => (
                    <tr key={orphan.userId}>
                      <td className="py-3 px-4">
                        <div className="text-sm font-medium text-gray-900">{orphan.name || t('users.nameFallback')}</div>
                        <div className="text-sm text-gray-600">{orphan.email}</div>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-700">
                        {orphan.createdAt ? formatDate(orphan.createdAt) : '-'}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => handleApproveOrphan(orphan.userId)}
                            disabled={approveOrphanMutation.isPending || rejectOrphanMutation.isPending}
                            className="btn btn-secondary text-xs px-3 py-2"
                          >
                            <Check className="w-4 h-4" />
                            {t('users.orphans.approve')}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleRejectOrphan(orphan.userId)}
                            disabled={approveOrphanMutation.isPending || rejectOrphanMutation.isPending}
                            className="btn btn-danger text-xs px-3 py-2"
                          >
                            <X className="w-4 h-4" />
                            {t('users.orphans.reject')}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Users Table */}
      {filteredUsers.length === 0 ? (
        <div className="card p-12 text-center">
          <UsersIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {searchTerm || roleFilter !== 'all' || statusFilter !== 'all'
              ? t('users.empty.none')
              : t('users.empty.noneRegistered')}
          </h3>
          <p className="text-gray-600 mb-4">
            {searchTerm || roleFilter !== 'all' || statusFilter !== 'all'
              ? t('users.empty.searchHint')
              : t('users.empty.firstHint')}
          </p>
          {(!searchTerm && roleFilter === 'all' && statusFilter === 'all') && (
            <button onClick={handleAdd} className="btn btn-primary">
              <Plus className="w-4 h-4" />
              {t('users.create')}
            </button>
          )}
        </div>
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">{t('users.fields.name')}</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">{t('users.fields.role')}</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">{t('users.fields.status')}</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">{t('users.fields.lastLogin')}</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">{t('users.fields.memberSince')}</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">{t('users.headerActions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {filteredUsers.map((u) => (
                  <tr key={u.id} className="hover:bg-gray-50 transition-colors">
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-3">
                        <div className="avatar avatar-gradient avatar-bordered w-10 h-10 font-semibold">
                          {u.name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U'}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {u.name || t('users.nameFallback')}
                          </div>
                          <div className="text-sm text-gray-600 flex items-center gap-1 flex-wrap">
                            <Mail className="w-3 h-3 flex-shrink-0" />
                            {u.email}
                            {adUserIds.has(u.id) && (
                              <span className="inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide bg-blue-100 text-blue-800 border border-blue-200">
                                <Building2 className="w-3 h-3" />
                                {t('users.adBadge')}
                              </span>
                            )}
                            {(u.emailDeliveryFailedAt != null || u.EmailDeliveryFailedAt != null) && (
                              <span
                                className="inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide bg-amber-100 text-amber-800 border border-amber-200"
                                title={
                                  u.emailDeliveryFailureMessage ||
                                  u.EmailDeliveryFailureMessage ||
                                  t('users.emailUndeliveredHint')
                                }
                              >
                                <MailWarning className="w-3 h-3" />
                                {t('users.emailUndeliveredBadge')}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <span className={clsx('badge', roleLabels[u.role]?.color || 'badge-gray')}>
                        <Shield className="w-3 h-3" />
                        {roleLabels[u.role]?.label || u.role}
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      <span
                        className={clsx(
                          'badge',
                          u.status === 'active' ? 'badge-success' : 'badge-gray'
                        )}
                      >
                        {u.status === 'active' ? t('users.status.enabledBadge') : t('users.status.noAccessBadge')}
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      <span className="text-sm text-gray-700">
                        {u.lastLoginAt
                          ? formatDate(u.lastLoginAt)
                          : t('users.fields.never')}
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      <span className="text-sm text-gray-700">
                        {u.createdAt
                          ? formatDate(u.createdAt)
                          : 'N/A'}
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      <button
                        onClick={() => handleEdit(u)}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <MoreVertical className="w-4 h-4 text-gray-600" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal */}
      <UserModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false)
          setSelectedUser(null)
        }}
        user={selectedUser}
      />

      {/* Associar usuário existente (somente admin global) */}
      {isGlobalUser && (
        <AssociateExistingUserModal
          isOpen={isAssociateModalOpen}
          onClose={() => setIsAssociateModalOpen(false)}
        />
      )}
    </div>
  )
}
